import React from 'react'
import { IoChevronBack, IoChevronForward, IoCheckmark, IoClose } from 'react-icons/io5'
import type { ApisPose } from '../types'
import './animations.css'

interface TutorialTopLayoutProps {
  text: string
  onPrev: () => void
  onNext: () => void
  onSkip: () => void
  onFinish: () => void
  isFirst: boolean
  isLast: boolean
  canNext?: boolean
  canSkip?: boolean
  anchorMissing?: boolean
  pose?: ApisPose
}

// Mapeamento de poses da Apis para arquivos de imagem
const APIS_IMAGES: Record<ApisPose, string> = {
  neutro: '/Imagens/Apis1.png',
  apontando: '/Imagens/Apis_Apontando.png',
  comemorando: '/Imagens/Apis_Festa.png',
  pensando: '/Imagens/Apis_Pensando.png',
  feliz: '/Imagens/Apis_Sorrindo.png',
  surpresa: '/Imagens/Apis_Surpresa.png',
  olhando_para_baixo: '/Imagens/Apis_Olhando_Para_Baixo.png',
}

const containerStyle: React.CSSProperties = {
  position: 'fixed',
  top: 24,
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 10001,
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'center',
  gap: 16,
  width: 'calc(100% - 32px)',
  maxWidth: 520,
  pointerEvents: 'none',
}

const leftColumnStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 12,
  flex: 1,
  pointerEvents: 'auto',
}

const bubbleStyle: React.CSSProperties = {
  maxWidth: 320,
  minWidth: 220,
  background: 'linear-gradient(135deg, #ffffff, #f8f9fa)',
  color: '#111',
  padding: '14px 16px',
  borderRadius: 14,
  border: '2px solid #8B5CF6',
  boxShadow: '0 4px 12px rgba(0,0,0,0.08), 0 12px 32px rgba(0,0,0,0.12)',
  fontSize: 14,
  lineHeight: 1.45,
  animation: 'slideInFromBottom 0.3s ease-out forwards',
  position: 'relative',
}

// Tail do balão (seta apontando para a Apis - à direita)
const bubbleTail: React.CSSProperties = {
  position: 'absolute',
  right: -8,
  top: '50%',
  transform: 'translateY(-50%)',
  width: 0,
  height: 0,
  borderLeft: '8px solid #8B5CF6',
  borderTop: '8px solid transparent',
  borderBottom: '8px solid transparent',
}

const controlsRowStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
  justifyContent: 'flex-start',
}

// Botão primário (Próximo, Concluir)
const btnPrimary: React.CSSProperties = {
  padding: '10px 20px',
  borderRadius: 8,
  background: 'linear-gradient(135deg, #8B5CF6, #7C3AED)',
  color: '#fff',
  border: 'none',
  cursor: 'pointer',
  fontWeight: 600,
  fontSize: 14,
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  transition: 'all 0.2s ease-out',
  boxShadow: '0 2px 8px rgba(139, 92, 246, 0.3)',
}

// Botão secundário (Voltar)
const btnSecondary: React.CSSProperties = {
  padding: '10px 20px',
  borderRadius: 8,
  background: 'transparent',
  color: '#8B5CF6',
  border: '2px solid #8B5CF6',
  cursor: 'pointer',
  fontWeight: 600,
  fontSize: 14,
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  transition: 'all 0.2s ease-out',
}

// Botão terciário (Pular)
const btnTertiary: React.CSSProperties = {
  padding: '10px 16px',
  borderRadius: 8,
  background: 'transparent',
  color: '#6B7280',
  border: 'none',
  cursor: 'pointer',
  fontWeight: 500,
  fontSize: 14,
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  transition: 'all 0.2s ease-out',
}

const apisStyle: React.CSSProperties = {
  width: 112,
  height: 'auto',
  pointerEvents: 'none',
  flexShrink: 0,
  animation: 'fadeInScale 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) forwards, floatVertical 4s ease-in-out 0.4s infinite',
}

const TutorialTopLayout: React.FC<TutorialTopLayoutProps> = ({
  text,
  onPrev,
  onNext,
  onSkip,
  onFinish,
  isFirst,
  isLast,
  canNext = true,
  canSkip = true,
  anchorMissing = false,
  pose = 'neutro',
}) => {
  const imageSrc = APIS_IMAGES[pose]
  return (
    <div style={containerStyle} data-tutorial-ui="1">
      <div style={leftColumnStyle}>
        <div id="tutorial-bubble" style={bubbleStyle} role="status" aria-live="polite" aria-atomic="true">
          {text}
          {anchorMissing && (
            <div style={{ marginTop: 12, padding: '8px 12px', background: '#FEF3C7', borderRadius: 8, fontSize: 13, color: '#92400E' }}>
              ⚠️ Elemento não disponível. Você pode estar offline ou o elemento ainda não carregou. Toque em <strong>Pular</strong> para continuar.
            </div>
          )}
          {/* Tail apontando para a Apis (acima) */}
          <div style={bubbleTail} aria-hidden="true" />
        </div>
        <div style={controlsRowStyle} aria-describedby="tutorial-bubble">
          {!isFirst && (
            <button
              type="button"
              style={btnSecondary}
              className="tutorial-btn-hover"
              onClick={onPrev}
              aria-label="Voltar"
              aria-describedby="tutorial-bubble"
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.05)'
                e.currentTarget.style.backgroundColor = 'rgba(139, 92, 246, 0.1)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)'
                e.currentTarget.style.backgroundColor = 'transparent'
              }}
            >
              <IoChevronBack size={16} />
              Voltar
            </button>
          )}
          {!isLast && (
            <button
              type="button"
              style={{ ...btnPrimary, opacity: canNext ? 1 : 0.5, cursor: canNext ? 'pointer' : 'not-allowed' }}
              className={canNext ? 'tutorial-btn-hover' : ''}
              onClick={onNext}
              aria-label="Próximo"
              aria-describedby="tutorial-bubble"
              disabled={!canNext}
              onMouseEnter={(e) => {
                if (canNext) {
                  e.currentTarget.style.transform = 'scale(1.05)'
                  e.currentTarget.style.filter = 'brightness(1.1)'
                }
              }}
              onMouseLeave={(e) => {
                if (canNext) {
                  e.currentTarget.style.transform = 'scale(1)'
                  e.currentTarget.style.filter = 'brightness(1)'
                }
              }}
            >
              Próximo
              <IoChevronForward size={16} />
            </button>
          )}
          {!isLast && (
            <button
              type="button"
              style={{ ...btnTertiary, opacity: canSkip ? 1 : 0.5, cursor: canSkip ? 'pointer' : 'not-allowed' }}
              className={canSkip ? 'tutorial-btn-hover' : ''}
              onClick={onSkip}
              aria-label="Pular"
              aria-describedby="tutorial-bubble"
              disabled={!canSkip}
              onMouseEnter={(e) => {
                if (canSkip) {
                  e.currentTarget.style.transform = 'scale(1.05)'
                  e.currentTarget.style.color = '#111'
                }
              }}
              onMouseLeave={(e) => {
                if (canSkip) {
                  e.currentTarget.style.transform = 'scale(1)'
                  e.currentTarget.style.color = '#6B7280'
                }
              }}
            >
              Pular
              <IoClose size={14} />
            </button>
          )}
          {isLast && (
            <button
              type="button"
              style={btnPrimary}
              className="tutorial-btn-hover"
              onClick={onFinish}
              aria-label="Concluir"
              aria-describedby="tutorial-bubble"
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.05)'
                e.currentTarget.style.filter = 'brightness(1.1)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)'
                e.currentTarget.style.filter = 'brightness(1)'
              }}
            >
              Concluir
              <IoCheckmark size={16} />
            </button>
          )}
        </div>
      </div>
      <img
        src={imageSrc}
        alt="Apis"
        style={apisStyle}
        loading="lazy"
        decoding="async"
      />
    </div>
  )
}

export default TutorialTopLayout
